import * as _ from 'lodash'
import { period_score, unified_match, unified_team_field } from 'adv-lib/src/models-mongod/pro/unified_match'
import { LsportsPeriod, LsportsResult, LsportsScoreBoard } from 'adv-lib/src/models-mongod/lsports/sport-event-livescore'
import {
  BaseballPeriodMatchStatus,
  BasketballPeriodMatchStatus,
  IceHockeyPeriodMatchStatus,
  LS_FIXTURE_STATUS,
  LS_PERIOD_TYPE,
  LS_STATUS_DESC,
  SoccorPeriodMatchStatus,
  TableTenisPeriodMatchStatus,
  TEAM_POSITION,
  VollyballPeriodMatchStatus,
} from 'adv-lib/src/v2/constants/common'
import { feed_unified } from 'adv-lib/src/models-mongod/feed/feed_unified'
import { BetLimitScoreInfo } from 'adv-lib/src/v2/interface/common'
import { MATCH_STATUS, SPORT_ID } from 'adv-protocol/src/GameCommon'

export interface HomeAndAwayTeam {
  home: unified_team_field
  away: unified_team_field
}

export function getHomeAndAwayTeam(competitors: unified_team_field[]): HomeAndAwayTeam {
  const home = _.find(competitors, { qualifier: 'home' })
  const away = _.find(competitors, { qualifier: 'away' })
  return { home, away }
}

/**
 * <p>소수점 둘째 자리 미만 버림 후 반환.</p>
 * <p>예) 1.332 => 1.33, 1.335 => 1.33, 1.339 => 1.33</p>
 * @param rateValue
 */
export function rateFloorCalculate(rateValue: number): number {
  return Math.floor(+(rateValue * 100).toFixed(12)) / 100
}

export function rateFloorCalculateWithExtraValue(rateValue: number, extraValue: number): number {
  return Math.floor((extraValue * +(rateValue * 100).toFixed(12)) / 100)
}

export function getHomeAndAwayScore(results: LsportsResult[]) {
  let homeScore = 0
  let awayScore = 0
  if (!_.isNil(results)) {
    for (let i = 0; i < _.size(results); i += 1) {
      const result = results[i]
      if (result.Position === TEAM_POSITION.HOME) {
        homeScore = Number(result.Value)
      } else {
        awayScore = Number(result.Value)
      }
    }
  }

  return {
    home: homeScore,
    away: awayScore,
  }
}

export function getPeriodName(period: LsportsPeriod): string {
  return LS_PERIOD_TYPE[period.Type || LS_PERIOD_TYPE.NONE]
}

export function getMatchStatusName(scoreBoard: LsportsScoreBoard): string {
  if (_.isNil(scoreBoard) || _.isNil(scoreBoard.CurrentPeriod)) {
    return LS_STATUS_DESC[LS_STATUS_DESC.UNKNOWN]
  }
  if (scoreBoard.Description) {
    return LS_STATUS_DESC[scoreBoard.Description]
  }
  return LS_PERIOD_TYPE[scoreBoard.CurrentPeriod]
}

export function getGameStatusName(scoreBoard: LsportsScoreBoard, feedUnified?: feed_unified): string {
  if (_.isNil(scoreBoard) && _.isNil(feedUnified)) {
    return LS_FIXTURE_STATUS[LS_FIXTURE_STATUS.NOT_STARTED]
  }
  if (_.isNil(scoreBoard) && !_.isNil(feedUnified)) {
    return LS_FIXTURE_STATUS[feedUnified.status]
  }
  return LS_FIXTURE_STATUS[scoreBoard.Status]
}

export function getGameStatusNameByFeedUnified(feedUnified: feed_unified): string {
  if (_.isNil(feedUnified)) {
    return LS_FIXTURE_STATUS[LS_FIXTURE_STATUS.NOT_STARTED]
  }
  return LS_FIXTURE_STATUS[feedUnified.status] || LS_FIXTURE_STATUS[LS_FIXTURE_STATUS.NOT_STARTED]
}

export function getMatchEndedDate(unifiedMatch: Partial<unified_match>): Date {
  let endedDateMatch = new Date()
  if (unifiedMatch && _.isArray(unifiedMatch.timelines)) {
    const foundTimeLine = unifiedMatch.timelines.find(timeLine => timeLine.type === 'match_ended')
    if (foundTimeLine) {
      endedDateMatch = foundTimeLine.time
    }
  }

  return endedDateMatch
}

/**
 * 경기의 현재 점수 중 큰 값이 제한점수를 넘어섰는지 유효성 체크
 * 세트별 점수인지 아닌지에 따라 점수를 가져오는게 다르다
 * @param params
 */
export const validateBetLimitScore = (params: {
  marketType: number,
  homeScore: number,
  awayScore: number,
  matchStatusCode: number,
  periodScores?: period_score[],
  betLimitScoreInfo?: BetLimitScoreInfo,
}): boolean => {
  let { homeScore, awayScore } = params
  const {
    marketType,
    matchStatusCode,
    betLimitScoreInfo,
    periodScores,
  } = params

  if (!betLimitScoreInfo) {
    return true
  }

  const { isPeriodScore, markets, periodLimitScores } = betLimitScoreInfo
  let { score: limitScore } = betLimitScoreInfo

  if (!_.includes(markets, marketType)) {
    return true
  }

  if (isPeriodScore && !_.isEmpty(periodScores)) {
    const periodScore = periodScores[periodScores.length - 1]
    const { matchStatus, period } = periodScore

    if (matchStatusCode !== matchStatus) {
      return true
    }

    homeScore = periodScore.homeScore
    awayScore = periodScore.awayScore

    const periodLimitScore = _.find(periodLimitScores, { period })
    limitScore = periodLimitScore?.score || limitScore
  }

  const currentScore = homeScore > awayScore ? homeScore : awayScore
  return currentScore < limitScore
}

const getPeriodMatchStatus = (sportId: number) => {
  let periodMatchStatus = []

  switch (sportId) {
    case SPORT_ID.SOCCER:
      periodMatchStatus = SoccorPeriodMatchStatus
      break
    case SPORT_ID.BASKETBALL:
      periodMatchStatus = BasketballPeriodMatchStatus
      break
    case SPORT_ID.BASEBALL:
      periodMatchStatus = BaseballPeriodMatchStatus
      break
    case SPORT_ID.VOLLEYBALL:
      periodMatchStatus = VollyballPeriodMatchStatus
      break
    case SPORT_ID.ICE_HOCKEY:
      periodMatchStatus = IceHockeyPeriodMatchStatus
      break
    case SPORT_ID.TABLE_TENNIS:
      periodMatchStatus = TableTenisPeriodMatchStatus
      break
    default:
      break
  }

  return periodMatchStatus
}

/**
 * 경기의 상태(matchStatusCode)에 따른 회차 (이닝, 세트, 피리오드 ... 등)를 구해오는 기능
 *
 * @param {Number} sportId
 * @param {Number} matchStatusCode
 * @param {object} periodScores - MatchTrack에 담긴 periodScore 정보 (회차로 구별되지 않을 때를 위해 사용)
 */
export const getMatchPeriodBySport = (
  sportId: number,
  matchStatusCode: number,
  periodScores?: period_score[],
): number => {
  let period = 0

  if (matchStatusCode !== MATCH_STATUS.NotStarted) {
    const periodMatchStatus = getPeriodMatchStatus(sportId)

    if (_.includes(periodMatchStatus, matchStatusCode)) {
      let periodIndex = _.indexOf(periodMatchStatus, matchStatusCode)

      if (sportId === SPORT_ID.BASEBALL) {
        periodIndex = (periodIndex / 2)
      }

      period = periodIndex + 1
    } else if (periodScores) {
      period = _.size(periodScores) + 1
    }
  }

  return period
}
